import { useEffect, useRef } from "react";

import HexMark from "./HexMark";
import { gsap, prefersReducedMotion } from "../lib/motion";
import "./Preloader.css";

/* ===========================================================================
   The page-load moment.

   The reference drops a static logo onto an overlay. Here the mark builds
   itself: the three hexagon cells draw in brand order, the circuit solders
   together, and the real wordmark rises underneath to complete the lockup.

   The hero starts animating the instant the curtain begins to lift, not after
   it lands, so the two moves overlap and nothing feels queued.
   =========================================================================== */

const MAX_WAIT = 700;

function heroReady() {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = resolve;
    img.onerror = resolve;
    /* Same srcset/sizes as the hero <picture> and the <link rel="preload">, so
       this probe resolves to whichever variant the browser actually chose and
       lands on a warm cache instead of pulling a second file. */
    img.sizes = "(max-width: 767px) 92vw, 400px";
    img.srcset =
      "/assets/images/hero-main-400.webp 400w, /assets/images/hero-main-600.webp 600w, /assets/images/hero-main-800.webp 800w";
    window.setTimeout(resolve, MAX_WAIT);
  });
}

export default function Preloader({ onComplete }) {
  const rootRef = useRef(null);
  const markRef = useRef(null);
  const wordmarkRef = useRef(null);
  const lockupRef = useRef(null);
  const curtainRef = useRef(null);
  const doneRef = useRef(false);

  useEffect(() => {
    /* The static #boot curtain in index.html carried first paint while the
       bundle parsed; this component owns the screen from here. */
    document.getElementById("boot")?.remove();

    /* Hand the page back the moment the curtain starts lifting. The release
       has to happen here, not in the effect cleanup: this component stays
       mounted for the life of the page (it only hides itself), so a cleanup
       that never runs would leave the body locked and the page unscrollable. */
    const finish = () => {
      if (doneRef.current) return;
      doneRef.current = true;
      delete document.body.dataset.locked;
      onComplete?.();
    };

    if (prefersReducedMotion()) {
      gsap.set(rootRef.current, { autoAlpha: 0, display: "none" });
      finish();
      return undefined;
    }

    document.body.dataset.locked = "true";

    const rings = markRef.current.querySelectorAll(".hex-mark__ring");
    const circuit = markRef.current.querySelectorAll(".hex-mark__circuit > *");

    /* Prime the dash so each hexagon can draw itself. */
    rings.forEach((ring) => {
      const len = ring.getTotalLength();
      gsap.set(ring, { strokeDasharray: len, strokeDashoffset: len });
    });
    gsap.set(circuit, { autoAlpha: 0, scale: 0.4, transformOrigin: "center" });
    gsap.set(wordmarkRef.current, { autoAlpha: 0, y: 22 });

    const build = gsap.timeline();

    /* The curtain is the page's LCP gate — render delay was 85% of mobile
       LCP — so the build is compressed to ~0.6s total. It still reads as a
       build; it just doesn't linger. */
    build
      .to(rings, { strokeDashoffset: 0, duration: 0.4, ease: "power2.inOut", stagger: 0.05 })
      .to(circuit, { autoAlpha: 1, scale: 1, duration: 0.24, ease: "back.out(2.6)", stagger: 0.02 }, 0.22)
      .to(wordmarkRef.current, { autoAlpha: 1, y: 0, duration: 0.3, ease: "power3.out" }, 0.3);

    let exit;

    Promise.all([build.then(), heroReady()]).then(() => {
      exit = gsap.timeline();
      exit
        .to(lockupRef.current, { y: -26, autoAlpha: 0, duration: 0.24, ease: "power2.in" })
        .to(
          curtainRef.current,
          {
            yPercent: -100,
            duration: 0.62,
            ease: "expo.inOut",
            /* Hand the page over as the curtain starts moving. */
            onStart: finish,
          },
          0.06,
        )
        .set(rootRef.current, { display: "none" });
    });

    return () => {
      build.kill();
      exit?.kill();
      delete document.body.dataset.locked;
    };
  }, [onComplete]);

  return (
    <div className="preloader" ref={rootRef} role="status" aria-live="polite">
      <span className="sr-only">Loading Intelligence Lab</span>

      <div className="preloader__curtain" ref={curtainRef}>
        <div className="preloader__lockup" ref={lockupRef}>
          <div className="preloader__mark-wrap" ref={markRef}>
            <HexMark className="preloader__mark" />
          </div>

          <img
            ref={wordmarkRef}
            className="preloader__wordmark"
            src="/assets/logo/wordmark-white.png"
            alt=""
            width="510"
            height="116"
          />
        </div>
      </div>
    </div>
  );
}
